import React, { useEffect, useMemo, useState } from "react";
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Badge,
  Table,
  Button,
  ButtonGroup,
  Popover,
  PopoverHeader,
  PopoverBody,
  Modal,
  ModalBody,
  ModalHeader,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { getOrgSummary, listPipelineProjects, createPipelineProject, updatePipelineProject, deletePipelineProject } from "../service";
import { updateRealOrder } from "../../project/service";
import { getRelativeTime } from "../../common/DateHelpers";
import staticData from "../data/org_summary_sample.json";
import ActivityLog from "./ActivityLog";

const USE_STATIC_DATA = false;

// Status buckets
const NOT_PLACED_STATUSES = ['draft', 'finalised', 'approved', 'confirmed'];
const PLACED_STATUSES = ['placed', 'production_planning', 'production',  'partially_delivered', 'delivered'];

const OrgSummaryPane = ({ organizationId, onClose }) => {
  const [data, setData] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "last_activity_at",
    direction: "desc",
  });
  const [selectedProject, setSelectedProject] = useState("");
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [viewFilter, setViewFilter] = useState("cart");
  const [showProjections, setShowProjections] = useState(true);
  const [pipelineProjects, setPipelineProjects] = useState([]);
  const [newPipeline, setNewPipeline] = useState({ name: "", value: "", month: "" });
  const [editingPipeline, setEditingPipeline] = useState(null);
  const [showAddPipelineForm, setShowAddPipelineForm] = useState(false);

  const toggleActivityLog = () => setShowActivityLog(!showActivityLog);
  const togglePopover = () => setPopoverOpen(!popoverOpen);

  useEffect(() => {
    if (organizationId) {
      if (USE_STATIC_DATA) {
        setData(staticData);
        setPipelineProjects([]);
      } else {
        getOrgSummary(organizationId).then((res) => setData(res));
        listPipelineProjects(organizationId).then((res) => setPipelineProjects(res || []));
      }
    }
  }, [organizationId]);

  const formatCurrency = (value) =>
    value === 0 || value == null ? "-" : `₹${(value / 100000).toFixed(2)}L`;

  const handleOrderDateChange = (projectId, monthVal) => {
    updateRealOrder(projectId, monthVal)
      .then(() => {
        setData((prev) => ({
          ...prev,
          projects: prev.projects.map((proj) =>
            proj.id === projectId ? { ...proj, order_date: monthVal } : proj
          ),
        }));
      })
      .catch((err) => console.error("Failed to update order_date", err));
  };

  const handleCreatePipeline = () => {
    if (!newPipeline.name || !newPipeline.value || !newPipeline.month) {
      alert("Please fill all fields");
      return;
    }
    createPipelineProject(organizationId, {
      name: newPipeline.name,
      value: parseInt(newPipeline.value), // Value in rupees
      month: newPipeline.month,
    })
      .then((res) => {
        setPipelineProjects([...pipelineProjects, res]);
        setNewPipeline({ name: "", value: "", month: "" });
        setShowAddPipelineForm(false);
      })
      .catch((err) => console.error("Failed to create pipeline project", err));
  };

  const handleUpdatePipeline = (id) => {
    if (!editingPipeline.name || !editingPipeline.value || !editingPipeline.month) {
      alert("Please fill all fields");
      return;
    }
    updatePipelineProject(id, {
      name: editingPipeline.name,
      value: parseInt(editingPipeline.value), // Value in rupees
      month: editingPipeline.month,
    })
      .then((res) => {
        setPipelineProjects(pipelineProjects.map((p) => (p.id === id ? res : p)));
        setEditingPipeline(null);
      })
      .catch((err) => console.error("Failed to update pipeline project", err));
  };

  const handleDeletePipeline = (id) => {
    if (!window.confirm("Are you sure you want to delete this pipeline project?")) {
      return;
    }
    deletePipelineProject(id)
      .then(() => {
        setPipelineProjects(pipelineProjects.filter((p) => p.id !== id));
      })
      .catch((err) => console.error("Failed to delete pipeline project", err));
  };

  const handlePipelineMonthChange = (id, monthVal) => {
    const pipeline = pipelineProjects.find((p) => p.id === id);
    if (!pipeline) return;

    updatePipelineProject(id, {
      name: pipeline.name,
      value: pipeline.value,
      month: monthVal,
    })
      .then((res) => {
        setPipelineProjects(pipelineProjects.map((p) => (p.id === id ? res : p)));
      })
      .catch((err) => console.error("Failed to update pipeline month", err));
  };

  const handleSort = (key) => {
    const isAsc = sortConfig.key === key && sortConfig.direction === "asc";
    setSortConfig({ key, direction: isAsc ? "desc" : "asc" });
  };

  const sortProjects = (projects) => {
    return [...projects].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === "last_activity_at") {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      } else if (typeof aVal === "string" || typeof bVal === "string") {
        aVal = (aVal || "").toLowerCase();
        bVal = (bVal || "").toLowerCase();
      } else {
        aVal = aVal ?? 0;
        bVal = bVal ?? 0;
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return "⇅";
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  const filteredProjects = useMemo(() => {
    const projects = data?.projects || [];
    const pipelines = showProjections ? pipelineProjects : [];

    // Transform pipeline projects to look like regular projects
    const pipelineAsProjects = pipelines.map((p) => ({
      id: `pipeline_${p.id}`,
      name: p.name,
      value: p.value,
      status: "Pipeline",
      last_activity_at: null,
      order_date: `${p.month}-01`,
      score: null,
      is_pipeline: true,
      pipeline_id: p.id,
    }));

    const allProjects = [...projects, ...pipelineAsProjects];

    return allProjects.filter((project) => {
      const projectStatus = project.status?.toLowerCase();
      const isPlaced = PLACED_STATUSES.includes(projectStatus);
      const projectOrderDate = project.order_date;

      const currentMonth = new Date().toISOString().slice(0, 7);
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      const nextMonthStr = nextMonth.toISOString().slice(0, 7);

      const projectMonth = projectOrderDate
        ? new Date(projectOrderDate).toISOString().slice(0, 7)
        : null;

      // Combined filter logic
      if (viewFilter === "draft") {
        // Draft: Not placed, month not set
        return !isPlaced && !projectOrderDate;
      } else if (viewFilter === "cart") {
        // Cart: Not placed, this month
        return !isPlaced && projectMonth === currentMonth;
      } else if (viewFilter === "later") {
        // Later: Not placed, next month or later
        return !isPlaced && projectMonth && (project.is_pipeline || projectMonth >= nextMonthStr);
      } else if (viewFilter === "placed") {
        // Placed: Placed
        return isPlaced;
      }

      return true;
    });
  }, [data, viewFilter, showProjections, pipelineProjects]);

  return (
    <>
      <Offcanvas
        isOpen={!!organizationId}
        toggle={onClose}
        direction="end"
        style={{ width: "50%", top: "0", marginTop: 0 }}
        className="border-start position-fixed"
      >
        <OffcanvasHeader toggle={onClose} className="border-bottom pb-2 mb-0">
          {data?.name || "Loading..."}
        </OffcanvasHeader>

        <div className="d-flex justify-content-between align-items-center bg-light px-3 py-2 border-bottom">
          <div className="d-flex align-items-center">
            <Badge
              color="secondary"
              pill
              className="text-uppercase small px-3 py-1"
            >
              {data?.status}
            </Badge>
          </div>
          <div>
            <Button
              id="usersBtn"
              color="light"
              size="sm"
              className="border"
              onClick={togglePopover}
            >
              <i className="fas fa-user"></i>
            </Button>
            <Popover
              placement="left"
              isOpen={popoverOpen}
              target="usersBtn"
              toggle={togglePopover}
            >
              <PopoverHeader>
                Users
                <Button close className="ms-auto" onClick={togglePopover} />
              </PopoverHeader>
              <PopoverBody>
                <Table bordered size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th className="bg-secondary text-white">Name</th>
                      <th className="bg-secondary text-white">Mobile</th>
                      <th className="bg-secondary text-white">Price Access</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.users || []).map((u, idx) => (
                      <tr key={idx}>
                        <td>{u.name}</td>
                        <td>{u.mobile}</td>
                        <td>{u.view_price ? "Yes" : "No"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </PopoverBody>
            </Popover>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-light px-3 py-2 border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <ButtonGroup size="sm">
              <Button
                color={viewFilter === "draft" ? "primary" : "outline-primary"}
                onClick={() => setViewFilter("draft")}
              >
                Draft
              </Button>
              <Button
                color={viewFilter === "cart" ? "primary" : "outline-primary"}
                onClick={() => setViewFilter("cart")}
              >
                Cart
              </Button>
              <Button
                color={viewFilter === "later" ? "primary" : "outline-primary"}
                onClick={() => setViewFilter("later")}
              >
                Later
              </Button>
              <Button
                color={viewFilter === "placed" ? "primary" : "outline-primary"}
                onClick={() => setViewFilter("placed")}
              >
                Placed
              </Button>
            </ButtonGroup>
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="projectionsToggleOrg"
                checked={showProjections}
                onChange={(e) => setShowProjections(e.target.checked)}
              />
              <label className="form-check-label fw-bold small" htmlFor="projectionsToggleOrg">
                Show Projections
              </label>
            </div>
          </div>
        </div>

        <OffcanvasBody className="p-3">
          <Table
            bordered
            responsive
            hover
            size="sm"
            style={{ fontSize: "0.95rem" }}
          >
            <thead>
              <tr>
                <th
                  className="bg-secondary text-white"
                  role="button"
                  onClick={() => handleSort("name")}
                >
                  Project {renderSortIcon("name")}
                </th>
                <th
                  className="bg-secondary text-white"
                  role="button"
                  onClick={() => handleSort("value")}
                >
                  Value {renderSortIcon("value")}
                </th>
                <th
                  className="bg-secondary text-white"
                  role="button"
                  onClick={() => handleSort("status")}
                >
                  Status {renderSortIcon("status")}
                </th>
                <th
                  className="bg-secondary text-white"
                  role="button"
                  onClick={() => handleSort("last_activity_at")}
                >
                  Last Activity {renderSortIcon("last_activity_at")}
                </th>
                <th className="bg-secondary text-white">Expected Month</th>
                <th
                  className="bg-secondary text-white"
                  role="button"
                  onClick={() => handleSort("score")}
                >
                  Score {renderSortIcon("score")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortProjects(filteredProjects).map((proj) => (
                <tr key={proj.id} style={{ whiteSpace: "nowrap" }} className={proj.is_pipeline ? "table-warning" : ""}>
                  <td>
                    {proj.is_pipeline ? (
                      <>
                        {editingPipeline && editingPipeline.id === proj.pipeline_id ? (
                          <Input
                            type="text"
                            bsSize="sm"
                            value={editingPipeline.name}
                            onChange={(e) => setEditingPipeline({ ...editingPipeline, name: e.target.value })}
                          />
                        ) : (
                          <>
                            {proj.name}
                            <Badge color="info" pill className="ms-2 small">Pipeline</Badge>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <a
                          href={`/projects/${proj.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-dark text-decoration-none"
                        >
                          {proj.name}
                        </a>{" "}
                        <span className="text-AAAAAA">#{proj.id}</span>
                      </>
                    )}
                  </td>
                  <td>
                    {editingPipeline && editingPipeline.id === proj.pipeline_id ? (
                      <Input
                        type="number"
                        bsSize="sm"
                        value={editingPipeline.value}
                        onChange={(e) => setEditingPipeline({ ...editingPipeline, value: e.target.value })}
                        placeholder="Rupees"
                      />
                    ) : (
                      formatCurrency(proj.value)
                    )}
                  </td>
                  <td>{proj.status}</td>
                  <td>{proj.is_pipeline ? "-" : getRelativeTime(proj.last_activity_at)}</td>
                  <td>
                    {proj.is_pipeline ? (
                      <input
                        type="month"
                        className="form-control form-control-sm"
                        value={
                          proj.order_date
                            ? new Date(proj.order_date).toISOString().slice(0, 7)
                            : ""
                        }
                        min={new Date().toISOString().slice(0, 7)}
                        max={(() => {
                          const date = new Date();
                          date.setMonth(date.getMonth() + 2);
                          return date.toISOString().slice(0, 7);
                        })()}
                        onChange={(e) =>
                          handlePipelineMonthChange(proj.pipeline_id, e.target.value)
                        }
                      />
                    ) : PLACED_STATUSES.includes(proj.status?.toLowerCase()) ? (
                      <span className="text-muted">-</span>
                    ) : (
                      <input
                        type="month"
                        className="form-control form-control-sm"
                        value={
                          proj.order_date
                            ? new Date(proj.order_date).toISOString().slice(0, 7)
                            : ""
                        }
                        min={new Date().toISOString().slice(0, 7)}
                        max={(() => {
                          const date = new Date();
                          date.setMonth(date.getMonth() + 2);
                          return date.toISOString().slice(0, 7);
                        })()}
                        onChange={(e) =>
                          handleOrderDateChange(proj.id, e.target.value)
                        }
                      />
                    )}
                  </td>
                  <td>
                    {proj.is_pipeline ? (
                      <div className="d-flex gap-1">
                        {editingPipeline && editingPipeline.id === proj.pipeline_id ? (
                          <>
                            <Button size="sm" color="success" onClick={() => handleUpdatePipeline(proj.pipeline_id)}>
                              <i className="fas fa-check"></i>
                            </Button>
                            <Button size="sm" color="secondary" onClick={() => setEditingPipeline(null)}>
                              <i className="fas fa-times"></i>
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              color="primary"
                              onClick={() =>
                                setEditingPipeline({
                                  id: proj.pipeline_id,
                                  name: proj.name,
                                  value: proj.value.toString(), // Value already in rupees
                                  month: proj.order_date ? proj.order_date.slice(0, 7) : "",
                                })
                              }
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                            <Button size="sm" color="danger" onClick={() => handleDeletePipeline(proj.pipeline_id)}>
                              <i className="fas fa-trash"></i>
                            </Button>
                          </>
                        )}
                      </div>
                    ) : (
                      <span
                        role="button"
                        onClick={() => {
                          setSelectedProject(proj);
                          toggleActivityLog();
                        }}
                      >
                        {proj.score}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Add Pipeline Button */}
          {!showAddPipelineForm && (
            <div className="mt-3">
              <Button color="success" size="sm" onClick={() => setShowAddPipelineForm(true)}>
                <i className="fas fa-plus me-2"></i>Add Pipeline Project
              </Button>
            </div>
          )}

          {/* Add New Pipeline Project Form */}
          {showAddPipelineForm && (
            <div className="mt-3 p-3 bg-light border rounded">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0">Add Pipeline Project</h6>
                <Button
                  close
                  onClick={() => {
                    setShowAddPipelineForm(false);
                    setNewPipeline({ name: "", value: "", month: "" });
                  }}
                />
              </div>
              <div className="row g-2">
                <div className="col-md-4">
                  <Input
                    type="text"
                    bsSize="sm"
                    placeholder="Project Name"
                    value={newPipeline.name}
                    onChange={(e) => setNewPipeline({ ...newPipeline, name: e.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <Input
                    type="number"
                    bsSize="sm"
                    placeholder="Value (₹)"
                    value={newPipeline.value}
                    onChange={(e) => setNewPipeline({ ...newPipeline, value: e.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <Input
                    type="month"
                    bsSize="sm"
                    value={newPipeline.month}
                    onChange={(e) => setNewPipeline({ ...newPipeline, month: e.target.value })}
                  />
                </div>
                <div className="col-md-2">
                  <Button color="primary" size="sm" block onClick={handleCreatePipeline}>
                    <i className="fas fa-check me-1"></i>Save
                  </Button>
                </div>
              </div>
            </div>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <Modal isOpen={showActivityLog} toggle={toggleActivityLog} size="xl">
        <ModalHeader>{selectedProject.name}</ModalHeader>
        <ModalBody>
          <ActivityLog
            projectId={selectedProject.id}
            getRelativeTime={getRelativeTime}
          />
        </ModalBody>
      </Modal>
    </>
  );
};

export default OrgSummaryPane;
